// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
/**
 * Deep-link helpers for SAPIENS calculators — Deployment 13.
 *
 * Usage in each calc component:
 *   const params = useDeepLinkParams({ ral: 'num', figli: 'int' });
 *   const [ral, setRal] = useState(params.ral ?? '35000');
 *   ...
 *   <ShareCalcButton getValues={() => ({ ral, figli })} slug="busta-paga" />
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Share2, Check } from 'lucide-react';

// Simple URL param sanitization. `spec` maps fieldName -> validator type.
//   'num'  → positive numeric (string), e.g. "30000" or "3.5"
//   'int'  → non-negative integer (string), e.g. "0" "1" "2" "3"
//   'year' → integer in [1947, currentYear]
//   ['enum', 'val1', 'val2', ...] → must match one of allowed values
export function useDeepLinkParams(spec) {
  const [params] = useSearchParams();
  const out = {};
  Object.entries(spec).forEach(([key, type]) => {
    const raw = params.get(key);
    if (raw == null) return;
    if (Array.isArray(type) && type[0] === 'enum') {
      const allowed = type.slice(1);
      if (allowed.includes(raw)) out[key] = raw;
      return;
    }
    if (type === 'num') {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) out[key] = String(n);
      return;
    }
    if (type === 'int') {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= 0) out[key] = String(n);
      return;
    }
    if (type === 'year') {
      const n = parseInt(raw, 10);
      const cy = new Date().getFullYear();
      if (Number.isFinite(n) && n >= 1947 && n <= cy) out[key] = String(n);
      return;
    }
  });
  return out;
}

// Check if all mandatory keys are present in URL (for auto-run).
export function hasAllParams(urlParams, mandatory) {
  return mandatory.every(k => urlParams[k] !== undefined && urlParams[k] !== '');
}

// Share button: builds /calcolatori/{slug}?... URL from current values.
export function ShareCalcButton({ slug, getValues, className = '', testid = 'share-calc-btn' }) {
  const [copied, setCopied] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState('');

  const handleClick = async () => {
    const values = getValues() || {};
    const usp = new URLSearchParams();
    Object.entries(values).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) usp.set(k, String(v));
    });
    const url = `${window.location.origin}/calcolatori/${slug}?${usp.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setFallbackUrl(url);
    }
  };

  return (
    <div className={`mt-4 ${className}`}>
      <button
        onClick={handleClick}
        data-testid={testid}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
        style={{ background: '#252523', color: '#E8E8E4', border: '1px solid rgba(201,168,76,0.3)' }}
      >
        {copied ? <Check className="w-3.5 h-3.5 text-[#3D7A52]" /> : <Share2 className="w-3.5 h-3.5" />}
        {copied ? 'Link copiato! 📋' : 'Condividi risultato'}
      </button>
      {fallbackUrl && (
        <div className="mt-2">
          <label className="block text-xs text-[#888780] mb-1">Copia questo link:</label>
          <input
            type="text"
            value={fallbackUrl}
            readOnly
            onFocus={e => e.target.select()}
            data-testid={`${testid}-fallback`}
            className="w-full px-3 py-2 rounded-lg text-xs text-[#E8E8E4]"
            style={{ background: '#1A1A18', border: '1px solid rgba(255,255,255,0.1)' }}
          />
        </div>
      )}
    </div>
  );
}

// Smooth scroll to result (used after auto-run via URL params).
export function scrollToResult(testid = 'calc-result-anchor') {
  setTimeout(() => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 350);
}
